import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getSubmissionDashboard } from '@ebalance/proto/bffuser/v1/submission_management_service-SubmissionManagementService_connectquery'
import {
  GetSubmissionDashboardRequestSchema,
  type GetSubmissionDashboardResponse,
} from '@ebalance/proto/bffuser/v1/submission_management_service_pb'
import { type UseQueryResult, keepPreviousData } from '@tanstack/react-query'

import { toProtoDate } from '~/models/dateTime'

/**
 * 提出管理ダッシュボード RPC (Design Note Decision 1)。対象日 (当日 / 翌日) ごとに
 * react-query がキャッシュするため、日断面タブの往復は再取得だけが起き
 * (Decision 11)、エリアタブの切替は同じ応答からの差し替えのみで再取得しない。
 *
 * `placeholderData: keepPreviousData`: 対象日の key が変わった直後 (server time
 * offset 確定・日付変更) に、前 key の応答を placeholder (`isPlaceholderData`) として
 * 残し、data が undefined になってタブごと unmount (focus 喪失) するのを防ぐ。
 * 日断面タブの切替では key は変わらない (当日・翌日は別 query として常駐)。
 * placeholder は前 key の値なので、消費側はインジケータ等に流用しないこと。
 *
 * `throwOnError: false`: flag OFF (プレースホルダ) / RPC error (読込エラー表示) を
 * 画面内で出し分ける必要があるため (Decision 9 / 19)、`useDemandDashboard` 等
 * 既存の一覧・ダッシュボード系ページと同じ throwOnError override。
 */
export function useSubmissionDashboardQuery(
  targetDate: Date,
): UseQueryResult<GetSubmissionDashboardResponse, ConnectError> {
  return useQuery(
    getSubmissionDashboard,
    create(GetSubmissionDashboardRequestSchema, {
      targetDate: toProtoDate(targetDate),
    }),
    { throwOnError: false, placeholderData: keepPreviousData },
  )
}
